import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Chart from 'react-apexcharts';
import { Calendar, TrendingUp, Clock, CheckCircle, User, BarChart3, Activity, Target, Users, Stethoscope, Wrench, ShieldCheck, Sparkles, Package, Truck } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import styles from './PersonalDashboard.module.css';
import commonStyles from '../../styles/common.module.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ChartSpinner } from '../Statistics/ChartSpinner';
import { getChartColors } from '../Statistics/chartUtils';
import Select from 'react-select';
import Loading from '../Loading';

const PersonalDashboard = () => {
  const { user } = useAuth();
  const [personalStats, setPersonalStats] = useState({});
  const [stageStats, setStageStats] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [users, setUsers] = useState([]);
  const [selectedCompareUser, setSelectedCompareUser] = useState(null);
  const [compareStats, setCompareStats] = useState(null);

  const formatApiDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    const selectedProjectId = localStorage.getItem('selected_project_id');
    if (selectedProjectId) params.append('project_id', selectedProjectId);
    if (startDate && endDate && startDate <= endDate) {
      params.append('start_date', formatApiDate(startDate));
      params.append('end_date', formatApiDate(endDate));
    }
    if (selectedCompareUser) {
      params.append('compare_user_id', selectedCompareUser.value);
    }
    return params;
  }, [startDate, endDate, selectedCompareUser]);

  const fetchUsers = async () => {
    try {
      const selectedProjectId = localStorage.getItem('selected_project_id');
      if (!selectedProjectId) {
        console.error('selected_project_id не найден в localStorage');
        toast.error('Выберите проект для загрузки пользователей');
        return;
      }
      const response = await api.get(`/core/users/list/?project_id=${selectedProjectId}`);
      const userOptions = response.data.map(u => ({
        value: u.id,
        label: u.full_name && u.full_name.trim() ? u.full_name : u.username
      }));
      setUsers(userOptions);
    } catch (err) {
      console.error('Ошибка при загрузке пользователей:', err);
      toast.error('Ошибка при загрузке пользователей для сравнения');
    }
  };

  const fetchPersonalStats = useCallback(async () => {
    try {
      setLoading(true);
      const params = getQueryParams();
      const response = await api.get(`/stats/personal-stats/?${params.toString()}`);
      
      const { user_stats, compare_user_stats, compare_user_name } = response.data;
      
      setPersonalStats({
        total_processed: user_stats.total_processed,
        completed: user_stats.completed,
        avg_time: user_stats.avg_time,
        efficiency: user_stats.efficiency,
        stage_details: user_stats.stage_details,
        role_stats: user_stats.role_stats,
        daily_stats: user_stats.daily_stats
      });
      
      setStageStats(user_stats.stage_details || []);
      setDailyStats(user_stats.daily_stats || []);
      
      if (compare_user_stats) {
        setCompareStats({
          ...compare_user_stats,
          compare_user_name
        });
      } else {
        setCompareStats(null);
      }
    } catch (err) {
      console.error('Ошибка при загрузке персональной статистики:', err);
      toast.error('Ошибка при загрузке статистики', {
        toastId: 'fetch-personal-stats-error',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [getQueryParams]);

  useEffect(() => {
    fetchUsers();
    fetchPersonalStats();
  }, [fetchPersonalStats, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(prevKey => prevKey + 1);
  };

  const stageChartOptions = useMemo(
    () => ({
      chart: {
        type: 'donut',
        fontFamily: 'Inter, system-ui, sans-serif',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
      },
      labels: stageStats.map((item) => item.stage),
      colors: getChartColors(2),
      stroke: {
        width: 2,
        colors: ['#fff'],
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '14px',
          fontWeight: 500,
          colors: ['#fff'],
        },
        formatter: (val, opts) => {
          return opts.w.globals.series[opts.seriesIndex];
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '60%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '16px',
                fontWeight: 600,
              },
              value: {
                show: true,
                fontSize: '22px',
                fontWeight: 700,
                formatter: (val) => val,
              },
              total: {
                show: true,
                label: 'Всего',
                fontSize: '16px',
                fontWeight: 600,
                formatter: (w) => {
                  return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                },
              },
            },
          },
        },
      },
      legend: {
        position: 'bottom',
        fontSize: '14px',
      },
    }),
    [stageStats]
  );

  const dailyChartOptions = useMemo(
    () => ({
      chart: {
        type: 'area',
        fontFamily: 'Inter, system-ui, sans-serif',
        toolbar: { show: false },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.1,
        },
      },
      xaxis: {
        categories: dailyStats.map((item) => 
          new Date(item.date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
        ),
        labels: {
          style: {
            fontSize: '12px',
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '12px',
          },
        },
      },
      colors: [getChartColors(0)[0]],
      grid: {
        borderColor: '#f1f5f9',
        strokeDashArray: 4,
      },
      tooltip: {
        shared: true,
        intersect: false,
      },
    }),
    [dailyStats]
  );

  const formatPeriod = () => {
    const start = startDate.toLocaleDateString('ru-RU');
    const end = endDate.toLocaleDateString('ru-RU');
    return `${start} — ${end}`;
  };

  const roleDisplayNames = {
    'приёмщик': 'Приемка',
    'Специалист по диагностике': 'Диагностика',
    'Инженер по ремонту': 'Ремонт',
    'Инженер по ремонту (возврат без ремонта)': 'Возврат без ремонта',
    'Специалист по QA': 'Контроль качества',
    'Специалист по клинингу': 'Клининг',
    'Специалист по упаковке': 'Упаковка',
    'Специалист по логистике': 'Отправки (партии)'
  };

  const roleIcons = {
    'приёмщик': <CheckCircle size={24} />,
    'Специалист по диагностике': <Stethoscope size={24} />,
    'Инженер по ремонту': <Wrench size={24} />,
    'Инженер по ремонту (возврат без ремонта)': <Wrench size={24} />,
    'Специалист по QA': <ShieldCheck size={24} />,
    'Специалист по клинингу': <Sparkles size={24} />,
    'Специалист по упаковке': <Package size={24} />,
    'Специалист по логистике': <Truck size={24} />
  };

  if (loading) {
    return <Loading message="Загрузка персональной статистики..." size="large" />;
  }

  const userRoles = (user?.roles || []).map(role => role.name);
  const isAdmin = userRoles.includes('admin') || Boolean(user?.is_staff) || Boolean(user?.is_superuser);

  return (
    <div className={`${commonStyles.pageContainer} ${commonStyles.fadeIn}`}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.titleSection}>
            <div className={styles.titleGroup}>
              <div className={styles.iconWrapper}>
                <User className={styles.titleIcon} />
              </div>
              <div>
                <h1 className={styles.pageTitle}>Моя статистика</h1>
                <p className={styles.pageSubtitle}>Персональная производительность за период {formatPeriod()}</p>
              </div>
            </div>
          </div>
          
          <div className={styles.headerActions}>
            <div className={styles.datePickerWrapper}>
              <Calendar size={16} />
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                dateFormat="dd.MM.yyyy"
                className={styles.dateInput}
                placeholderText="Дата начала"
              />
              <span className={styles.dateSeparator}>—</span>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                dateFormat="dd.MM.yyyy"
                className={styles.dateInput}
                placeholderText="Дата окончания"
              />
            </div>
            <Select
              options={users}
              value={selectedCompareUser}
              onChange={setSelectedCompareUser}
              placeholder="Сравнить с..."
              className={styles.compareSelect}
              classNamePrefix="react-select"
              isClearable
            />
            <button className={styles.refreshButton} onClick={handleRefresh}>
              <Activity size={18} />
              <span>Обновить</span>
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={styles.summaryRow}>
        {Object.entries(personalStats.role_stats || {}).map(([role, count]) => {
          if ((isAdmin || userRoles.includes(role.replace(' (возврат без ремонта)', ''))) && count > 0) {
            return (
              <div key={role} className={`${styles.summaryCard} ${styles[role.toLowerCase().replace(' ', '_').replace('_(возврат_без_ремонта)', '_return')]}`}>
                <div className={styles.summaryIcon}>
                  {roleIcons[role] || <Target size={24} />}
                </div>
                <div className={styles.summaryContent}>
                  <h3 title="Количество уникальных аппаратов, для которых сотрудник завершил этот этап за выбранный период">{roleDisplayNames[role] || role}</h3>
                  <div className={styles.summaryValue}>
                    {count}
                    {compareStats && (
                      <span className={styles.compareValue}>
                        (vs {compareStats.role_stats[role] || 0} у {compareStats.compare_user_name})
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })}
        <div className={`${styles.summaryCard} ${styles.total}`}>
          <div className={styles.summaryIcon}>
            <Activity size={24} />
          </div>
          <div className={styles.summaryContent}>
            <h3 title="Любое действие сотрудника с аппаратом за период, даже если этап ещё не завершён">Аппараты с активностью</h3>
            <div className={styles.summaryValue}>
              {personalStats.total_processed || 0}
              {compareStats && (
                <span className={styles.compareValue}>
                  (vs {compareStats.total_processed || 0} у {compareStats.compare_user_name})
                </span>
              )}
            </div>
          </div>
        </div>
        <div className={`${styles.summaryCard} ${styles.completed}`}>
          <div className={styles.summaryIcon}>
            <CheckCircle size={24} />
          </div>
          <div className={styles.summaryContent}>
            <h3 title="Из аппаратов с активностью: сейчас готовы или уже отправлены">Сейчас готовы / отправлены</h3>
            <div className={styles.summaryValue}>
              {personalStats.completed || 0}
              {compareStats && (
                <span className={styles.compareValue}>
                  (vs {compareStats.completed || 0} у {compareStats.compare_user_name})
                </span>
              )}
            </div>
          </div>
        </div>
        <div className={`${styles.summaryCard} ${styles.avgTime}`}>
          <div className={styles.summaryIcon}>
            <Clock size={24} />
          </div>
          <div className={styles.summaryContent}>
            <h3 title="Средний интервал между зафиксированными этапами обработки">Средний интервал</h3>
            <div className={styles.summaryValue}>
              {personalStats.avg_time ? `${personalStats.avg_time.toFixed(1)}ч` : '0ч'}
              {compareStats && (
                <span className={styles.compareValue}>
                  (vs {compareStats.avg_time ? `${compareStats.avg_time.toFixed(1)}ч` : '0ч'} у {compareStats.compare_user_name})
                </span>
              )}
            </div>
          </div>
        </div>
        <div className={`${styles.summaryCard} ${styles.efficiency}`}>
          <div className={styles.summaryIcon}>
            <TrendingUp size={24} />
          </div>
          <div className={styles.summaryContent}>
            <h3 title="Доля завершённых аппаратов среди уникальных обработанных">Доля завершённых</h3>
            <div className={styles.summaryValue}>
              {personalStats.efficiency ? `${personalStats.efficiency.toFixed(1)}%` : '0%'}
              {compareStats && (
                <span className={styles.compareValue}>
                  (vs {compareStats.efficiency ? `${compareStats.efficiency.toFixed(1)}%` : '0%'} у {compareStats.compare_user_name})
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.metricExplanation}>
        <strong>Почему показатели могут отличаться?</strong>
        <span>«Аппараты с активностью» учитывает любое действие сотрудника за период. Карточки этапов учитывают только аппараты, где сотрудник завершил соответствующий этап. Поэтому аппарат, взятый в работу, но ещё не переведённый дальше, попадёт только в первый показатель.</span>
      </div>

      {/* Charts */}
      <div className={styles.chartsContainer}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <div className={styles.chartTitleGroup}>
              <div className={styles.chartIconWrapper}>
                <BarChart3 size={20} />
              </div>
              <h3 className={styles.chartTitle}>Обработка по этапам</h3>
            </div>
          </div>
          <div className={styles.chartBody}>
            {stageStats.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <BarChart3 size={48} />
                </div>
                <h3>Нет данных</h3>
                <p>За выбранный период нет обработанного оборудования</p>
              </div>
            ) : (
              <Chart
                options={stageChartOptions}
                series={stageStats.map((item) => item.count)}
                type="donut"
                height={350}
                className={styles.chart}
              />
            )}
          </div>
        </div>

        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <div className={styles.chartTitleGroup}>
              <div className={styles.chartIconWrapper}>
                <Activity size={20} />
              </div>
              <h3 className={styles.chartTitle}>Активность по дням</h3>
            </div>
          </div>
          <div className={styles.chartBody}>
            {dailyStats.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <Activity size={48} />
                </div>
                <h3>Нет данных</h3>
                <p>За выбранный период нет активности</p>
              </div>
            ) : (
              <Chart
                options={dailyChartOptions}
                series={[{
                  name: 'Обработано',
                  data: dailyStats.map((item) => item.count)
                }]}
                type="area"
                height={350}
                className={styles.chart}
              />
            )}
          </div>
        </div>
      </div>

      {/* Detailed Stats Table */}
      {personalStats.stage_details && personalStats.stage_details.length > 0 && (
        <div className={styles.tableContainer}>
          <div className={styles.tableHeader}>
            <div className={styles.tableTitleGroup}>
              <div className={styles.tableIconWrapper}>
                <Target size={20} />
              </div>
              <h3 className={styles.sectionTitle}>Детальная статистика по этапам</h3>
            </div>
          </div>
          <div className={styles.tableWrapper}>
            <div className={styles.tableScrollWrapper}>
              <table className={styles.statsTable}>
                <thead>
                  <tr>
                    <th>Этап</th>
                    <th>Обработано</th>
                    {compareStats && <th>Обработано ({compareStats.compare_user_name})</th>}
                    <th>Среднее время (ч)</th>
                    {compareStats && <th>Среднее время ({compareStats.compare_user_name})</th>}
                  </tr>
                </thead>
                <tbody>
                  {personalStats.stage_details.map((stage, index) => {
                    const compareStage = compareStats?.stage_details.find(s => s.stage === stage.stage);
                    return (
                      <tr key={index} className={styles.tableRow}>
                        <td className={styles.stageName}>{stage.stage}</td>
                        <td className={styles.totalValue}>{stage.count}</td>
                        {compareStats && (
                          <td className={styles.totalValue}>{compareStage ? compareStage.count : 0}</td>
                        )}
                        <td>{stage.avg_time ? stage.avg_time.toFixed(1) : '0.0'}</td>
                        {compareStats && (
                          <td>{compareStage && compareStage.avg_time ? compareStage.avg_time.toFixed(1) : '0.0'}</td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalDashboard;
